export type LngLat = [number, number]; // [lng, lat] GeoJSON order

export type GeoArea =
  | { type: "Polygon"; coordinates: LngLat[][] }
  | { type: "MultiPolygon"; coordinates: LngLat[][][] };

export interface Location {
  id: string;
  name: string;
  geoArea?: GeoArea | null;
}

export interface LocationsResponse {
  responses: Location[];
}

export type PostcodeLevel = 'AREA' | 'DISTRICT' | 'SECTOR';

export interface PostCode {
  code: string;
  level: PostcodeLevel;
  parent: string | null;
  geoArea: GeoArea;
}

interface PostcodesResponse {
  responses: PostCode[];
}

export interface MatchResponse {
  count: number;
}

export interface SurveyData {
  locations: string[];
  postcodes?: string[];
  min_bedrooms?: string | number;
  max_bedrooms?: string | number;
  min_price?: string | number;
  max_price?: string | number;
  let_available_date?: string;
  furnish_types?: string[];
  min_bathrooms?: string | number;
  max_bathrooms?: string | number;
  flat_size_min?: string | number;
  deposit_max?: string | number;
  exclude_noisy_streets?: boolean;
}

export interface CreateSearchCriteriaResponse {
  success: boolean;
  searchCriteriaId?: string;
}

export class ListingsSearchCriteriaApi {
  constructor(private apiBaseUrl: string) {}

  async fetchLocations(opts?: { includeGeoArea?: boolean }): Promise<Location[]> {
    const includeGeoArea = opts?.includeGeoArea === true;
    const url = new URL(`${this.apiBaseUrl}/api/locations`, window.location.origin);

    if (includeGeoArea) {
      url.searchParams.set("includeGeoArea", "true");
    }

    const response = await fetch(url.toString(), {
      method: "GET",
      headers: { "Accept": "application/json" },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch locations: ${response.statusText}`);
    }

    const data: LocationsResponse = await response.json();
    return data.responses;
  }

  async fetchPostcodes(): Promise<PostCode[]> {
    const response = await fetch(`${this.apiBaseUrl}/api/postcodes`, {
      method: "GET",
      headers: { "Accept": "application/json" },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch postcodes: ${response.statusText}`);
    }

    const data: PostcodesResponse = await response.json();
    return data.responses;
  }

  async matchListings(criteria: SurveyData): Promise<MatchResponse> {
    const response = await fetch(`${this.apiBaseUrl}/api/properties/match`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(criteria)
    });

    if (!response.ok) {
      throw new Error(`Failed to match properties: ${response.statusText}`);
    }

    return response.json();
  }

  async submitSearchCriteria(userId: string, criteria: SurveyData): Promise<CreateSearchCriteriaResponse> {
    const response = await fetch(`${this.apiBaseUrl}/api/properties-alert?userId=${userId}`, {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(criteria)
    });

    if (!response.ok) {
      throw new Error(`Failed to submit search criteria: ${response.statusText}`);
    }

    return response.json();
  }
}
